import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bot, Workflow, ShieldCheck, ArrowRight } from 'lucide-react';
import { FeatureCard } from '../components/FeatureCard';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { CookieBanner } from '../components/CookieBanner';
import { useLanguage } from '../context/LanguageContext';

// Minimalist office image for CTA
const CTA_BG = 'https://customer-assets-4nw71qhi.emergentagent.net/job_insolvio-landing/artifacts/1kxoir7b_iStock-1141813702%20%281%29.webp';

// Logo
const LOGO_URL = '/insolvio-logo-cropped.png';

export default function LandingPage() {
  const { t, language } = useLanguage();
  const CONTACT_EMAIL = 'info@insolvio.se';
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const openMailto = () => {
    const subject = language === 'sv' 
      ? 'Demo-förfrågan - Insolvio'
      : 'Demo Request - Insolvio';
    const body = language === 'sv'
      ? 'Hej,\n\nJag skulle vilja begära en demo av Insolvio.\n\nMed vänliga hälsningar'
      : 'Hello,\n\nI would like to request a demo of Insolvio.\n\nBest regards';
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const features = [
    {
      icon: Bot,
      title: t('features.aiTitle'),
      description: t('features.aiDescription')
    },
    {
      icon: Workflow,
      title: t('features.workflowTitle'),
      description: t('features.workflowDescription')
    },
    {
      icon: ShieldCheck,
      title: t('features.securityTitle'),
      description: t('features.securityDescription')
    }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: '#fdfdfd' }} data-testid="landing-page">
      {/* Global ambient background - flows through entire page */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Subtle grid pattern - very light */}
        <div 
          className="absolute top-0 left-0 right-0 opacity-[0.04]"
          style={{
            height: '100vh',
            backgroundImage: `linear-gradient(#262626 1px, transparent 1px), linear-gradient(90deg, #262626 1px, transparent 1px)`,
            backgroundSize: '80px 80px',
            maskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
          }}
        />
        
        {/* Ambient green orb - top right */}
        <div 
          className="absolute rounded-full blur-3xl animate-orb-1"
          style={{ 
            width: '60vw', 
            maxWidth: '800px',
            aspectRatio: '1',
            backgroundColor: '#1c8a56',
            top: '-10%',
            right: '-15%',
          }}
        />
        
        {/* Ambient green orb - middle left (transitions between hero and features) */}
        <div 
          className="absolute rounded-full blur-3xl animate-orb-2 hidden sm:block"
          style={{ 
            width: '50vw', 
            maxWidth: '600px',
            aspectRatio: '1',
            backgroundColor: '#1c8a56',
            top: '60vh',
            left: '-15%',
          }}
        />
        
        {/* Ambient green orb - bottom (transitions to CTA) */}
        <div 
          className="absolute rounded-full blur-3xl animate-orb-3 hidden sm:block"
          style={{ 
            width: '55vw', 
            maxWidth: '700px',
            aspectRatio: '1',
            backgroundColor: '#1c8a56',
            top: '140vh',
            right: '-10%',
          }}
        />
      </div>

      {/* Navigation */}
      <nav 
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={scrolled 
          ? { backgroundColor: 'rgba(253, 253, 253, 0.85)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(229, 229, 229, 0.5)' }
          : { backgroundColor: 'transparent' }
        }
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-5">
          <div className="flex items-center justify-between gap-3">
            <img
              src={LOGO_URL}
              alt="Insolvio"
              className="h-8 md:h-10 flex-shrink-0"
              style={{ filter: 'invert(1)' }}
              data-testid="nav-logo" />

            <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
              <LanguageSwitcher />
              <button
                onClick={openMailto}
                className="rounded-md px-4 md:px-5 py-2 md:py-2.5 text-sm font-medium transition-all duration-200 whitespace-nowrap"
                style={{ backgroundColor: '#1c8a56', color: '#ffffff' }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                data-testid="nav-request-demo-btn">
                {t('nav.requestDemo')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        id="hero"
        className="relative min-h-screen flex items-center justify-center px-6 pt-20 md:pt-0"
        data-testid="hero-section">
        
        <div className="relative z-10 text-center max-w-4xl mx-auto">
          <p 
            className="eyebrow mb-4 md:mb-6"
            style={{ color: '#1c8a56' }}
          >
            {language === 'sv' ? 'KONKURSADMINISTRATION' : 'BANKRUPTCY ADMINISTRATION'}
          </p>

          <h1
            className="mb-5 md:mb-6 leading-tight whitespace-pre-line"
            style={{ 
              fontFamily: 'Outfit, sans-serif',
              fontSize: 'clamp(32px, 5vw, 60px)',
              fontWeight: 700,
              color: '#262626',
              letterSpacing: '-0.01em',
              lineHeight: '1.1'
            }}
            data-testid="hero-headline">

            {t('hero.title')}
          </h1>
          <p 
            className="mb-8 md:mb-10 max-w-2xl mx-auto leading-relaxed"
            style={{ color: '#6b6b6b', fontSize: 'clamp(16px, 1.5vw, 18px)', lineHeight: '1.7', letterSpacing: '0.025em' }}
          >
            {t('hero.description')}
          </p>
          <button
            onClick={openMailto}
            className="inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-medium transition-all duration-200"
            style={{ backgroundColor: '#1c8a56', color: '#ffffff' }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            data-testid="hero-request-demo-btn">

            {t('nav.requestDemo')}
            <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>
      </section>

      {/* Features Section */}
      <section 
        id="features" 
        className="relative py-16 md:py-32"
        data-testid="features-section"
      >
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
            <p 
              className="eyebrow mb-4"
              style={{ color: '#1c8a56' }}
            >
              {language === 'sv' ? 'FUNKTIONER' : 'FEATURES'}
            </p>
            <h2
              className="mb-4 md:mb-6"
              style={{ 
                fontFamily: 'Outfit, sans-serif',
                fontSize: 'clamp(26px, 4vw, 42px)',
                fontWeight: 600,
                color: '#262626',
                letterSpacing: '0em',
                lineHeight: '1.2'
              }}
              data-testid="features-headline"
            >
              {t('features.title')}
            </h2>
            <p 
              className="mx-auto"
              style={{ color: '#6b6b6b', fontSize: 'clamp(15px, 1.5vw, 18px)', lineHeight: '1.6', letterSpacing: '0.025em' }}
            >
              {t('features.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {features.map((feature) =>
              <FeatureCard key={feature.title} {...feature} />
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="relative overflow-hidden"
        data-testid="cta-section">

        <div className="grid grid-cols-1 md:grid-cols-[45%_55%] min-h-[500px] relative z-10">
          {/* Left: Text content */}
          <div className="flex items-center px-6 md:pl-16 md:pr-12 py-16 md:py-32 order-2 md:order-1">
            <div className="max-w-md">
              <p 
                className="eyebrow mb-4"
                style={{ color: '#1c8a56' }}
              >
                {language === 'sv' ? 'KOM IGÅNG' : 'GET STARTED'}
              </p>
              <h2
                className="mb-6 whitespace-pre-line"
                style={{ 
                  fontFamily: 'Outfit, sans-serif',
                  fontSize: 'clamp(28px, 4vw, 42px)',
                  fontWeight: 600,
                  color: '#262626',
                  letterSpacing: '0em',
                  lineHeight: '1.2'
                }}
                data-testid="cta-headline">

                {t('cta.title')}
              </h2>
              <p 
                className="mb-8"
                style={{ color: '#6b6b6b', fontSize: '16px', lineHeight: '28px', letterSpacing: '0.025em' }}
              >
                {language === 'sv' 
                  ? 'Kontakta oss för att se hur Insolvio kan effektivisera din konkursadministration.'
                  : 'Get in touch to see how Insolvio can streamline your bankruptcy administration.'
                }
              </p>
              <button
                onClick={openMailto}
                className="inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-medium transition-all duration-200"
                style={{ backgroundColor: '#1c8a56', color: '#ffffff' }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                data-testid="cta-request-demo-btn"
              >
                {t('nav.requestDemo')}
                <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
          </div>

          {/* Right: Image */}
          <div 
            className="relative min-h-[240px] md:min-h-[500px] order-1 md:order-2"
            style={{
              backgroundImage: `url(${CTA_BG})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            {/* Gradient overlay - desktop: from left, mobile: from bottom */}
            <div 
              className="absolute inset-0 hidden md:block"
              style={{ 
                background: 'linear-gradient(to right, #fdfdfd 0%, rgba(253, 253, 253, 0.5) 10%, transparent 30%)',
              }}
            />
            <div 
              className="absolute inset-0 md:hidden"
              style={{ 
                background: 'linear-gradient(to bottom, transparent 60%, rgba(253, 253, 253, 0.5) 85%, #fdfdfd 100%)',
              }}
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer 
        className="relative py-8 z-10" 
        data-testid="footer"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm" style={{ color: '#6b6b6b', letterSpacing: '0.025em' }}>
              {t('footer.copyright')}
            </p>
            <div className="flex items-center gap-6 flex-wrap justify-center">
              <Link
                to="/privacy"
                className="text-sm transition-colors"
                style={{ color: '#6b6b6b' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#1c8a56'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#6b6b6b'}
                data-testid="footer-privacy-link">
                {t('footer.privacy')}
              </Link>
              <Link
                to="/cookies"
                className="text-sm transition-colors"
                style={{ color: '#6b6b6b' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#1c8a56'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#6b6b6b'}
                data-testid="footer-cookies-link">
                {t('footer.cookies')}
              </Link>
              <Link
                to="/trust-center"
                className="text-sm transition-colors"
                style={{ color: '#6b6b6b' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#1c8a56'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#6b6b6b'}
                data-testid="footer-trust-center-link">
                {t('footer.trustCenter')}
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Cookie Banner */}
      <CookieBanner />
    </div>
  );
}
